import type { MouseEvent } from 'react'

import { zerlege } from '../lib/auszeichnung'
import { findeLinks, linkPruefung } from '../lib/links'
import { rueckfrage } from './Rueckfrage'
import { useTexte } from '../texte'

/**
 * Der Text einer Nachricht — mit Auszeichnung, aber ohne HTML.
 *
 * **Zerlegt und als Text gesetzt, nie über `dangerouslySetInnerHTML`.** Der
 * Text kommt von Menschen und geht an Menschen; ein Chat, in dem jemand
 * Markup unterbringen kann, ist keiner. Die Regel selbst steht in
 * `lib/auszeichnung.ts` — hier steht nur, welches Stück welche Klasse
 * bekommt.
 *
 * **One component for every place a post's words appear** (1.87.2): until
 * then it lived in `Bubble`, and the quote above a reply, the reply bar in
 * the composer and the browser's book showed the signs instead -- from use,
 * "~abgezockt~" with its tildes in a quote.
 *
 * **Links first, marks second** (1.113.0): an address is cut out before
 * `zerlege()` sees the text, otherwise the `_` in `…/a_b_c` turned half of
 * it italic. `links` says whose text this is -- the server says it, from the
 * role (`links_from`) -- and without it an address stays text (the quote,
 * the reply bar), whole and without marks.
 */
export function Ausgezeichnet({ text, links }: { text: string; links?: 'author' | 'reader' }) {
  return (
    <>
      {findeLinks(text).map((stueck, i) =>
        stueck.art !== 'link' || stueck.adresse === undefined ? (
          <Zeichen key={i} text={stueck.text} />
        ) : links === undefined ? (
          // Without `links` an address is text -- but whole, without marks.
          <span key={i}>{stueck.text}</span>
        ) : (
          <Link key={i} text={stueck.text} adresse={stueck.adresse} von={links} />
        ),
      )}
    </>
  )
}

function Zeichen({ text }: { text: string }) {
  return (
    <>
      {zerlege(text).map((stueck, i) =>
        stueck.art === 'text' ? (
          <span key={i}>{stueck.text}</span>
        ) : (
          <span key={i} className={`aus aus--${stueck.art}`}>
            {stueck.text}
          </span>
        ),
      )}
    </>
  )
}

/**
 * A link that opens in the browser, after the sheet.
 *
 * **A tap opens the sheet, a long press stays the bubble's.** A post that is
 * nothing but a link must still be changed and deleted (from use: "wie kann
 * man einen Beitrag löschen, bearbeiten, der nur einen Link enthält?"). The
 * link stops nothing, so `useLangdruck` at the row sees the press; once the
 * menu stands, its veil lies over the link and takes the release -- the
 * click never reaches it. Measured with a finger and with a mouse
 * (`linkblatt.mjs`): a timer that threw away "the click after a long press"
 * stayed green when switched off, and is not here for that reason.
 *
 * **No `stopPropagation` either, measured:** a tap on a bubble does nothing
 * outside the delete selection, and in it the row's children take no pointer
 * at all (`.bubble-row--waehlbar`), so a tap on a link selects the post.
 */
function Link({ text, adresse, von }: { text: string; adresse: string; von: 'author' | 'reader' }) {
  const t = useTexte()

  function beimTipp(event: MouseEvent) {
    event.preventDefault()
    const pruefung = linkPruefung(adresse)
    if (pruefung === null) return
    void rueckfrage(t.chat.link.frage(pruefung.host), t.chat.link.oeffnen, {
      details: [text],
      warnungen: [
        ...(von === 'reader' ? [{ schluessel: 'mitlesend', text: t.chat.link.vonMitlesend }] : []),
        ...pruefung.warnungen.map((w) => ({ schluessel: w, text: t.chat.link.warnung[w] ?? w })),
      ],
      art: 'normal',
      merkmal: 'link',
      // Inside the tap on the button, not after the promise (`Zusatz`).
      beimJa: () => {
        window.open(pruefung.adresse, '_blank', 'noopener,noreferrer')
      },
    })
  }

  return (
    <a
      className="textlink"
      href={adresse}
      target="_blank"
      rel="noopener noreferrer external"
      referrerPolicy="no-referrer"
      draggable={false}
      data-link={von}
      onClick={beimTipp}
      // The middle button opened the address without the sheet and its
      // warnings (1.187.0, review 2026-10b B8). A click with Ctrl or Cmd
      // comes through `onClick` and is held there.
      onAuxClick={(event) => {
        if (event.button === 1) beimTipp(event)
      }}
    >
      {text}
    </a>
  )
}
